import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

const monoFont = "'IBM Plex Mono', monospace";

interface Props {
  onClose: () => void;
  onInviteSent: () => void;
}

type ModalPhase = 'form' | 'share';

export function InviteModal({ onClose, onInviteSent }: Props) {
  const [email, setEmail] = useState('');
  const [plan, setPlan] = useState('free');
  const [channel, setChannel] = useState('');
  const [notes, setNotes] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [phase, setPhase] = useState<ModalPhase>('form');

  const handleGenerate = async () => {
    setError('');
    setSending(true);

    const { data: session } = await supabase.auth.getSession();
    const token = crypto.randomUUID();

    const insertData: any = {
      token,
      plan,
      invited_by: session.session?.user.id,
    };
    if (email.trim()) insertData.email = email.trim();
    if (channel.trim()) insertData.channel = channel.trim();
    if (notes.trim()) insertData.notes = notes.trim();

    const { error: insertError } = await supabase.from('invites').insert(insertData);

    if (insertError) {
      setError('Erro ao criar convite.');
      setSending(false);
      return;
    }

    const link = `https://www.criaaa.com/convite/${token}`;
    setInviteLink(link);

    // Send invite email if email was provided
    if (email.trim()) {
      await supabase.functions.invoke('send-transactional-email', {
        body: {
          type: 'invite-notification',
          data: { to: email.trim(), inviteUrl: link },
        },
      });
    }

    setPhase('share');
    setSending(false);
  };

  const handleCopy = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    onInviteSent();
  };

  const labelStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    letterSpacing: '0.08em', color: 'rgba(247,246,242,0.5)', marginBottom: 6, display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont, fontSize: 14, fontWeight: 300, width: '100%',
    padding: '10px 12px', border: '1px solid rgba(247,246,242,0.1)',
    backgroundColor: 'rgba(247,246,242,0.04)', color: 'rgba(247,246,242,0.85)',
  };

  const planBtnStyle = (isActive: boolean): React.CSSProperties => ({
    fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
    padding: '6px 14px', border: `1px solid ${isActive ? 'rgba(247,246,242,0.4)' : 'rgba(247,246,242,0.1)'}`,
    backgroundColor: isActive ? 'rgba(247,246,242,0.1)' : 'transparent',
    color: isActive ? '#F7F6F2' : 'rgba(247,246,242,0.4)', cursor: 'pointer',
    transition: 'all 0.15s ease',
  });

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center',
      backgroundColor: 'rgba(0,0,0,0.7)',
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: '#1a1a1a', padding: 32, maxWidth: 420, width: '100%' }}>

        {phase === 'share' ? (
          <div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(247,246,242,0.4)', marginBottom: 24 }}>
              CONVITE GERADO
            </div>
            <div style={{
              fontFamily: monoFont, fontSize: 13, fontWeight: 300, padding: '12px',
              backgroundColor: 'rgba(247,246,242,0.06)', border: '1px solid rgba(247,246,242,0.1)',
              color: 'rgba(247,246,242,0.7)', wordBreak: 'break-all', marginBottom: 16,
            }}>
              {inviteLink}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              <span style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.3)', marginRight: 'auto' }}>
                Válido por 72 horas.
              </span>
              <button onClick={handleCopy}
                style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  padding: '8px 16px', border: '1px solid rgba(247,246,242,0.3)',
                  backgroundColor: 'transparent', color: copied ? '#00E87A' : '#F7F6F2', cursor: 'pointer',
                }}>
                {copied ? 'COPIADO ✓' : 'COPIAR LINK'}
              </button>
              <button onClick={handleClose}
                style={{
                  fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase',
                  padding: '8px 16px', border: '1px solid rgba(247,246,242,0.15)',
                  backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer',
                }}>
                FECHAR
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(247,246,242,0.4)', marginBottom: 24 }}>
              NOVO CONVITE
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={labelStyle}>E-mail (opcional)</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="email@exemplo.com" />
              </div>
              <div>
                <label style={labelStyle}>Plano</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['free', 'pro', 'custom'].map(p => (
                    <button key={p} onClick={() => setPlan(p)} style={planBtnStyle(plan === p)}>
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label style={labelStyle}>Canal de origem (opcional)</label>
                <input value={channel} onChange={e => setChannel(e.target.value)} style={inputStyle} placeholder="ex: Instagram, evento SXSW..." />
              </div>
              <div>
                <label style={labelStyle}>Nota interna (opcional)</label>
                <input value={notes} onChange={e => setNotes(e.target.value)} style={inputStyle} placeholder="ex: fotógrafo de moda, amigo do João" />
              </div>

              {error && <div style={{ fontFamily: monoFont, fontSize: 12, color: '#FF3F2D' }}>{error}</div>}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button onClick={onClose}
                  style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '1px solid rgba(247,246,242,0.15)', backgroundColor: 'transparent', color: 'rgba(247,246,242,0.5)', cursor: 'pointer' }}>
                  CANCELAR
                </button>
                <button onClick={handleGenerate} disabled={sending}
                  style={{ fontFamily: monoFont, fontSize: 12, fontWeight: 300, textTransform: 'uppercase', padding: '8px 16px', border: '1px solid rgba(247,246,242,0.3)', backgroundColor: 'transparent', color: '#F7F6F2', cursor: sending ? 'wait' : 'pointer' }}>
                  {sending ? 'GERANDO...' : 'GERAR LINK'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
